import { Interaction } from '../apis';
import {
  ElementRole,
  InteractionQuality,
  PathSegment,
  TrackingData,
} from '../types';
import {
  hasMaskedContent,
  isSensitiveText,
  resolveAccessibleName,
  resolveRole,
  toSegmentName,
} from './accessibility';
import { resolveAction } from './resolveAction';
import { resolveHost } from './resolveHost';
import { resolveImportance } from './resolveImportance';
import { capScope, resolveScope, toSegment } from './resolveScope';
import { readSourceAnchor } from './sourceAnchor';

const DEV_TOOL_SELECTOR = '[data-confidence-devbar]';

const IGNORED_SELECTOR = '[data-track-ignore]';

const INERT_SELECTOR = '[aria-disabled="true"], :disabled';

const ANNOTATED_SELECTOR = '[data-track-id][data-track-action]';

const ROW_LEAF = 'item';

export type ActivationKind = 'pointer' | 'keyboard';

const isElement = (node: EventTarget): node is Element =>
  typeof (node as Element).closest === 'function';

const PERSONAL_PATH = /@/;

const linkOf = (host: Element): Element =>
  host.tagName === 'TR' ? (host.querySelector('a[href]') ?? host) : host;

const resourcePathOf = (host: Element): string => {
  const href = linkOf(host).getAttribute('href') ?? '';
  if (!href || PERSONAL_PATH.test(href) || hasMaskedContent(host)) {
    return '';
  }
  return href;
};

const pageData = (host: Element, pageId: string): TrackingData => {
  const pageViewId = document.body.dataset.trackDataPageViewId;
  const resourcePath = resourcePathOf(host);
  return {
    pageId,
    ...(pageViewId ? { pageViewId } : {}),
    ...(resourcePath ? { resourcePath } : {}),
  };
};

const resolveQuality = ({
  name,
  role,
  sensitive,
}: {
  name: string;
  role: ElementRole | null;
  sensitive: boolean;
}): InteractionQuality => {
  if (name && role) {
    return 'resolved';
  }
  return !name && sensitive && role ? 'suppressed' : 'degraded';
};

export const resolveInteraction = (
  event: Event,
  activation: ActivationKind,
  pageId: string,
): Interaction | null => {
  const target = event.target;
  if (target && isElement(target) && target.closest(ANNOTATED_SELECTOR)) {
    return null;
  }

  const host = resolveHost(event);
  if (
    !host ||
    host.closest(DEV_TOOL_SELECTOR) ||
    host.closest(IGNORED_SELECTOR) ||
    host.matches(INERT_SELECTOR)
  ) {
    return null;
  }

  const role = resolveRole(host);
  const source = readSourceAnchor(host);
  const visibleName = resolveAccessibleName(host);
  const sensitive = isSensitiveText(host, visibleName);
  const stableName = source?.stableName ?? '';
  const resolvedName =
    sensitive || stableName
      ? toSegmentName(stableName)
      : toSegmentName(visibleName);
  const name = resolvedName || (role === 'row' ? ROW_LEAF : '');
  const { segments, data, declaredNames } = resolveScope(host);
  const pageSegment = toSegment('main', pageId);
  const hasPageScope = declaredNames.has(pageSegment.name);
  const scope: PathSegment[] = capScope(
    hasPageScope ? segments : [pageSegment, ...segments],
  );

  const action = resolveAction(host);

  return {
    elementPath: [...scope, toSegment(role ?? 'generic', name)],
    inferred: true,
    type: activation === 'keyboard' ? 'keypress' : 'click',
    action,
    data: { ...data, ...pageData(host, pageId) },
    quality: resolveQuality({ name, role, sensitive }),
    importance: resolveImportance({
      host,
      role,
      action,
      marked: source?.element === host ? source.importance : null,
    }),
    sourceAnchor: source?.anchor ?? null,
  };
};
